import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ProtectedRoute } from './ProtectedRoute';
import { protectedRoutes, publicRoutes, portalRoutes } from './routeConfig';
import { LoginPage } from '../pages/LoginPage';

const SuperAdminLayout = React.lazy(() => import('../pages/SuperAdminLayout'));
const SuperAdminOverview = React.lazy(() => import('../pages/SuperAdminOverview'));
const SuperAdminTenantsPage = React.lazy(() => import('../pages/SuperAdminTenantsPage'));
const SuperAdminPlansPage = React.lazy(() => import('../pages/SuperAdminPlansPage'));
const SuperAdminSubscriptionsPage = React.lazy(() => import('../pages/SuperAdminSubscriptionsPage'));
const SuperAdminBillingPage = React.lazy(() => import('../pages/SuperAdminBillingPage'));
const SuperAdminIndustriesPage = React.lazy(() => import('../pages/SuperAdminIndustriesPage'));
const SuperAdminModuleMappingPage = React.lazy(() => import('../pages/SuperAdminModuleMappingPage'));
const BusinessActivitiesPage = React.lazy(() => import('../pages/BusinessActivitiesPage'));
const LandingPage = React.lazy(() => import('../pages/LandingPage'));
const SignupPage = React.lazy(() => import('../pages/SignupPage'));
const OnboardingWizard = React.lazy(() => import('../pages/OnboardingWizard'));
const BillingCheckoutPage = React.lazy(() => import('../pages/BillingCheckoutPage'));
const BillingHistoryPage = React.lazy(() => import('../pages/BillingHistoryPage'));
const MockPaymentPage = React.lazy(() => import('../pages/MockPaymentPage'));

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
  </div>
);

export const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Login Route */}
        <Route
          path="/login"
          element={
            user ? (
              user.is_super_admin ? <Navigate to="/super-admin" replace /> :
              user.role === 'driver' ? <Navigate to="/driver-mobile" replace /> : <Navigate to="/dashboard" replace />
            ) : (
              <LoginPage />
            )
          }
        />

        {/* Public Routes */}
        {publicRoutes.filter(r => r.path !== '/login').map(({ path, component: Component }) => (
          <Route key={path} path={path} element={<Component />} />
        ))}

        <Route path="/track/:trackingCode" element={
          <Suspense fallback={<LoadingFallback />}>
            {React.createElement(React.lazy(() => import('../pages/CustomerTrackingPage')))}
          </Suspense>
        } />

        {/* Protected Routes */}
        {protectedRoutes.map(({ path, component: Component }) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute>
                <Component />
              </ProtectedRoute>
            }
          />
        ))}

        {/* Customer Portal Routes */}
        <Route path={portalRoutes.login.path} element={<portalRoutes.login.component />} />
        <Route path={portalRoutes.layout.path} element={<portalRoutes.layout.component />}>
          {portalRoutes.children.map(({ path, component: Component }) => (
            <Route key={path} path={path} element={<Component />} />
          ))}
        </Route>

        {/* Driver alias redirect */}
        <Route path="/driver" element={<Navigate to="/driver-mobile" replace />} />

        {/* Super Admin Routes */}
        <Route path="/super-admin" element={<SuperAdminLayout />}>
          <Route index element={<SuperAdminOverview />} />
          <Route path="tenants" element={<SuperAdminTenantsPage />} />
          <Route path="plans" element={<SuperAdminPlansPage />} />
          <Route path="subscriptions" element={<SuperAdminSubscriptionsPage />} />
          <Route path="billing" element={<SuperAdminBillingPage />} />
          <Route path="industries" element={<SuperAdminIndustriesPage />} />
          <Route path="module-mapping" element={<SuperAdminModuleMappingPage />} />
          <Route path="users" element={<div className="text-slate-400">إدارة المستخدمين (قريباً)</div>} />
        </Route>

        {/* SaaS public pages */}
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/onboarding" element={<ProtectedRoute><OnboardingWizard /></ProtectedRoute>} />
        <Route path="/billing/checkout" element={<ProtectedRoute><BillingCheckoutPage /></ProtectedRoute>} />
        <Route path="/billing/history" element={<ProtectedRoute><BillingHistoryPage /></ProtectedRoute>} />
        <Route path="/mock-pay/:tx" element={<ProtectedRoute><MockPaymentPage /></ProtectedRoute>} />
        <Route path="/settings/business-activities" element={<ProtectedRoute><BusinessActivitiesPage /></ProtectedRoute>} />

        {/* Root: landing for guests, dashboard for authed users */}
        <Route path="/" element={user ? <Navigate to={user.is_super_admin ? '/super-admin' : '/dashboard'} replace /> : <LandingPage />} />

        {/* 404 Catch-all */}
        <Route path="*" element={<Navigate to={user ? '/dashboard' : '/'} replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
